import { Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';

import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';

const MICROSOFT_ENTRA_TENANT_ID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type Props = {
  opened: boolean;
  onClose: () => void;
  onConfirm: (sharepointTenantId?: string) => void | Promise<void>;
  confirmLoading?: boolean;
};

const SharePointConnectModal = ({ opened, onClose, onConfirm, confirmLoading = false }: Props) => {
  const [sharepointTenantId, setSharepointTenantId] = useState('');
  const normalizedSharepointTenantId = sharepointTenantId.trim();
  const sharepointTenantIdError =
    normalizedSharepointTenantId && !MICROSOFT_ENTRA_TENANT_ID_REGEX.test(normalizedSharepointTenantId)
      ? 'Enter a valid Microsoft Entra tenant ID GUID.'
      : null;
  const confirmDisabled = confirmLoading || Boolean(sharepointTenantIdError);

  useEffect(() => {
    if (!opened) {
      setSharepointTenantId('');
    }
  }, [opened]);

  const handleClose = () => {
    if (!confirmLoading) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (sharepointTenantIdError) {
      return;
    }

    try {
      await Promise.resolve(onConfirm(normalizedSharepointTenantId || undefined));
      if (!confirmLoading) {
        onClose();
      }
    } catch {
      // Let the caller handle request state; keep the modal open for retry.
    }
  };

  return (
    <StyledModal
      opened={opened}
      onClose={handleClose}
      title="Connect SharePoint"
      closeOnClickOutside={!confirmLoading}
      closeOnEscape={!confirmLoading}
    >
      <Stack gap="sm">
        <Text size="sm" c="#757575">
          Optional. Enter the Microsoft Entra tenant ID for the SharePoint tenant you want to connect to.
        </Text>
        <StyledTextInput
          label="Microsoft Entra tenant ID"
          placeholder="tenant GUID"
          value={sharepointTenantId}
          onChange={(event) => setSharepointTenantId(event.currentTarget.value)}
          error={sharepointTenantIdError}
        />
        <Text size="sm" c="#757575">
          Find it in Microsoft Entra admin center: Entra ID {'>'} Overview {'>'} Properties.
        </Text>
        <ModalConfirmationButtons
          onClose={handleClose}
          onConfirm={handleConfirm}
          confirmText="Continue"
          cancelText="Cancel"
          confirmLoading={confirmLoading}
          confirmDisabled={confirmDisabled}
          closeOnConfirm={false}
          cancelButtonProps={{ disabled: confirmLoading }}
        />
      </Stack>
    </StyledModal>
  );
};

export default SharePointConnectModal;
